/*
  A multi-select that saves on every click (Assign people on the card; Person / Project / Location in the
  task sheet).

  Without this, each click built its list from the task in props, and props only change once the PATCH
  returns — so a second click during a slow save sent a list without the first pick, and that pick was
  silently lost. Here:
    • the selection lives locally from the first click until every save for it has finished
      (the UI shows it right away);
    • every save sends the WHOLE list as it stands after that click;
    • saves run one after another, so the server always ends with the last list the user saw.
  When the queue is empty the server's value (props) is the truth again — including after a failed save.

  `scopeKey` (the task id) keeps a pending selection from leaking onto another task when the same
  component instance switches tasks (the sheet).
*/
import { useCallback, useEffect, useRef, useState } from 'react';

export function useQueuedSelection(scopeKey, serverIds, save) {
  const [draft, setDraft] = useState(null); // { key, ids } while saves are pending
  const draftRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  const pendingRef = useRef(0);
  const serverRef = useRef(serverIds);
  const saveRef = useRef(save);
  const mountedRef = useRef(true);
  serverRef.current = serverIds;
  saveRef.current = save;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const selected = draft && draft.key === scopeKey ? draft.ids : serverIds;

  const toggle = useCallback((id, context) => {
    const current = draftRef.current && draftRef.current.key === scopeKey ? draftRef.current.ids : serverRef.current;
    const adding = !current.includes(id);
    const next = adding ? [...current, id] : current.filter((item) => item !== id);
    const entry = { key: scopeKey, ids: next };
    draftRef.current = entry;
    setDraft(entry);
    pendingRef.current += 1;
    const saveForThisTask = saveRef.current; // the save of the task that was clicked, even if the view moves on
    const run = () => saveForThisTask(next, { id, adding, context });
    queueRef.current = queueRef.current
      .then(run)
      .catch(() => {
        // the caller reports its own errors; the queue keeps going and props win once it is empty
      })
      .finally(() => {
        pendingRef.current -= 1;
        if (pendingRef.current === 0) {
          draftRef.current = null;
          if (mountedRef.current) setDraft(null);
        }
      });
    return queueRef.current;
  }, [scopeKey]);

  return [selected, toggle];
}

export default useQueuedSelection;
